import * as React from 'react'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/next'
import { t, tc } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as hooks from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'
import { CATEGORY, EVENT_TYPE, SITE, useTracking } from '@owl-nest/shadow'

import { useDisplayMode } from '../hooks/useDisplayMode'

import * as UFE from '../UFE'

export type StickyNavigationProps = {
  className?: string
  isSticky: boolean
  project: models.project.ProjectForOwner
}

function StickyNavigationComponent({ className, isSticky, project }: StickyNavigationProps): React.ReactElement {
  const isClientSide = hooks.useClientSide()
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  const isMember = models.project.isUserMember(project)
  const subscriptionName = models.project.getSubscriptionName(project, UFE.USER_LOCALE as api.Lang)
  const isSubscriptionFree = models.project.getSubscriptionType(project) === 'free'

  const sectionNames = {
    about: t('About us'),
    posts: t('Posts'),
    subscriptions: t('Memberships'),
  }

  const [sections, setSections] = React.useState([sectionNames.subscriptions, sectionNames.posts, sectionNames.about])
  const [currentSection, setCurrentSection] = React.useState(sections[0])
  const [isOpen, setIsOpen] = React.useState(false)

  const tracking = useTracking()

  React.useEffect(() => {
    let lastScrollTop = 0

    const handleScroll = () => {
      const sectionIds = ['subscriptions', 'posts', 'about']
      let visibleSectionId = null

      const isScrollingDown = window.scrollY > lastScrollTop
      lastScrollTop = window.scrollY

      if (isScrollingDown) {
        sectionIds.forEach((sectionId) => {
          const section = document.getElementById(sectionId)
          if (section) {
            const rect = section.getBoundingClientRect()
            if (rect.top >= 0 && rect.top < 150) {
              visibleSectionId = sectionId
            }
          }
        })
      } else {
        sectionIds.reverse().forEach((sectionId) => {
          const section = document.getElementById(sectionId)
          if (section) {
            const rect = section.getBoundingClientRect()
            if (rect.bottom > 80 && rect.bottom < window.innerHeight) {
              visibleSectionId = sectionId
            }
          }
        })
      }

      if (visibleSectionId) {
        setCurrentSection(sectionNames[visibleSectionId as keyof typeof sectionNames])
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  React.useEffect(() => {
    if (isClientSide) {
      // Collect sections in homepage and order them according to their position on the page
      const sectionElements = document.querySelectorAll('.membership-section')
      const sectionArray = [...sectionElements]
      sectionArray.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
      setSections(sectionArray.map((section) => section.id))
    }
  }, [isClientSide])

  const scrollIntoViewWithOffset = (section: string, offset: number) => {
    const sectionElement = document.getElementById(section)

    if (sectionElement) {
      window.scrollTo({
        behavior: 'smooth',
        top: sectionElement.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
      })
    }
  }

  const handleToggle: () => void = () => {
    setIsOpen(!isOpen)
    if (isOpen === true) {
      scrollIntoViewWithOffset(sections[0], 85)
    }
  }
  const handleToggleIcon: () => void = () => {
    setIsOpen(!isOpen)
  }

  const handleSectionClick = (section: string) => {
    scrollIntoViewWithOffset(section, 85)
    setIsOpen(false)
  }

  let message
  if (isMember) {
    message = (
      <>
        <span>
          {isSubscriptionFree
            ? t('You are a free member')
            : t(`You are a %(subscriptionName)s member`, {
                subscriptionName,
              })}
        </span>
      </>
    )
  } else {
    message = (
      <span>
        {tc('Join the community from [price] per month!', {
          price: (
            <plume.LocaleMoneyDisplayer
              rates={UFE.CURRENCIES.RATES}
              amount={project.lowest_contribution_amount}
              currency={project.currency}
              targetCurrency={project.currency}
              localeFormat={UFE.USER_LOCALE}
            />
          ),
        })}
      </span>
    )
  }

  return (
    <>
      <StickyNavigationWrapper className={className} isEditMode={isEditMode} isSticky={isSticky}>
        <Inner>
          <Left>
            <Accordion
              title={isOpen ? sectionNames[sections[0] as keyof typeof sectionNames] : currentSection}
              onToggle={handleToggle}
              onToggleIcon={handleToggleIcon}
              open={isOpen}
            >
              <ul>
                {sections
                  .filter((section) => section !== sections[0])
                  .map((section) => (
                    <li key={section} onClick={() => handleSectionClick(section)}>
                      <plume.styles.copy.M>{sectionNames[section as keyof typeof sectionNames]}</plume.styles.copy.M>
                    </li>
                  ))}
              </ul>
            </Accordion>
          </Left>
          <Right>
            {isMember && <plume.glyphs.fill.Success size={18} />}
            <plume.styles.copy.S>{message}</plume.styles.copy.S>
            {!isMember ? (
              <plume.RoundedButton
                onClick={() => {
                  trackAndRedirect('ClickBecomeMember')
                }}
                icon={<plume.glyphs.stroke.Plus size={18} />}
              >
                {t('Become a member')}
              </plume.RoundedButton>
            ) : isSubscriptionFree ? (
              <plume.RoundedButton
                onClick={() => {
                  trackAndRedirect('ClickSeeMemberships')
                }}
              >
                {tc('Subscribe from [amount] / month', {
                  amount: (
                    <plume.LocaleMoneyDisplayer
                      rates={UFE.CURRENCIES.RATES}
                      amount={project.lowest_contribution_amount}
                      currency={project.currency}
                      targetCurrency={project.currency}
                      localeFormat={UFE.USER_LOCALE}
                    />
                  ),
                })}
              </plume.RoundedButton>
            ) : null}
          </Right>
        </Inner>
      </StickyNavigationWrapper>

      {isMember && isSubscriptionFree && (
        <CTASticky isSticky={isSticky}>
          <plume.RoundedButton
            fitToParent
            onClick={() => {
              trackAndRedirect('ClickSeeMemberships')
            }}
          >
            {tc('Subscribe from [amount] / month', {
              amount: (
                <plume.LocaleMoneyDisplayer
                  rates={UFE.CURRENCIES.RATES}
                  amount={project.lowest_contribution_amount}
                  currency={project.currency}
                  targetCurrency={project.currency}
                  localeFormat={UFE.USER_LOCALE}
                />
              ),
            })}
          </plume.RoundedButton>
        </CTASticky>
      )}
      {!isMember && (
        <CTASticky isSticky={isSticky}>
          <plume.RoundedButton
            onClick={() => {
              trackAndRedirect('ClickBecomeMember')
            }}
            fitToParent
            icon={<plume.glyphs.stroke.Plus size={18} />}
          >
            {t('Become a member')}
          </plume.RoundedButton>
        </CTASticky>
      )}
    </>
  )

  function trackAndRedirect(eventAction: string): void {
    tracking.track({
      event: EVENT_TYPE.GENERIC,
      eventCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventAction: eventAction,
      siteCategory: SITE.ULULE,
      pageCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventCallback: () => {
        if (typeof window !== 'undefined') {
          window.location.href = '#subscriptions'
        }
      },
    })
  }
}

export const StickyNavigation = styled(StickyNavigationComponent)``

export const StickyNavigationWrapper = styled.div<{ isEditMode: boolean; isSticky: boolean }>`
  align-items: center;
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  height: 50px;
  justify-content: center;
  margin-inline: auto;
  position: sticky;
  top: 0px;
  will-change: transform;
  z-index: 100;
  margin-top: -60px;
  margin-bottom: 10px;

  transition-property: opacity, transform;
  transition-duration: 0.1s;
  transition-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${({ isSticky }) => {
    return isSticky
      ? css`
          transform: scaleY(1);
          transform-origin: 0% 0%;
          opacity: 1;
        `
      : css`
          transform: scaleY(0);
          transform-origin: 0% 0%;
          opacity: 0;
        `
  }};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    height: 65px;
    margin-bottom: -5px;
  }
`

export const Left = styled.div`
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: fit-content;
  }
`

export const Right = styled.div`
  display: none;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: flex;
    align-items: center;
    gap: 16px;

    ${plume.styles.copy.S} {
      span {
        font-weight: 600; // HACK: Irregular font manipulation. We'll be adding copy.S.semiBold, so this is just by anticipation.
      }
    }
  }

  ${plume.glyphs.fill.Success} {
    fill: ${({ theme }) => theme?.colors?.primary ?? plume.COLORS.BRAND_INDIGO};
    margin-right: -8px;
  }
`

export const Accordion = styled(plume.Accordion)`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  width: 100%;
  padding: 25px 32px 25px;
  border: none;
  border-radius: 0;

  ul {
    padding: 0;
    margin: 22px 0 0 0;
    list-style: none;
  }

  li:not(:last-child) {
    margin-block: 16px;
  }

  li:last-child {
    margin-bottom: -2px;
  }

  ${plume.styles.copy.M} {
    cursor: pointer;
  }

  ${plume.glyphs.stroke.CaretDown}, ${plume.glyphs.stroke.CaretUp} {
    margin-top: 3px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    border: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
    border-radius: 4px;
    box-shadow: none;
    width: 163px;
    padding: 18px 16px 18px;
  }
`

export const Inner = styled.div`
  display: flex;
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    justify-content: space-between;
    max-width: 758px;
  }
  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    max-width: 900px;
  }

  ${Accordion} {
    position: fixed;
    top: 0;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      top: 7.5px;
    }
  }
`

export const CTASticky = styled.div<{ isSticky: boolean }>`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  position: fixed;
  bottom: 0;
  z-index: 500;
  height: 65px;
  width: 100%;
  transform-origin: 0% 100%;
  visibility: ${(props) => (props.isSticky ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isSticky ? 1 : 0)};
  transform: ${(props) => (props.isSticky ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.25s ease-in-out;
  will-change: transform;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: none;
  }
`
